import React from 'react';
import { useSelector } from 'react-redux';
import { i18n } from 'utils/i18n';
import { loadingOverlay } from 'utils/dom';
import { getFormattedPrice, getFormattedZeroPrice } from 'utils/prices';
import { analyticsInteraction } from 'utils/analytics';
import { guaranteedResCardRequiredSelector } from 'selectors/reservationSelectors';
import { getDidPrepay, appliedCancelFeeDetailsSelector } from 'selectors/reservationFlowSelectors';
import { cancelReservation } from 'actions/reservation/cancelReservation';
import Modal from 'components/Modal';
import { ModalFooter } from 'components/Modal/ModalFooter';
import { ANALYTICS, MODAL, MODAL_BUTTON_TYPES, MODAL_THEMES } from 'constants';
import FormattedPrice from 'components/FormattedPrice';
import ServiceErrors from 'components/ServiceErrors';
import useActions from 'hooks/useActions';

const { ACCEPT, DECLINE } = MODAL_BUTTON_TYPES;

/**
 * ConfirmCancelReservationModal - confirms that user wants to cancel reservation, showing any fees this may entail
 * @prop {React.FunctionComponent} ModalTitle
 * @param {object} props - React Props
 * @param {object} props.cancelReservation - dispatch cancelReservation action
 * @param {string} props.prepaySelected - prepay flag
 * @param {object}  props.appliedCancelFeeDetails - object regarding cancellation fee applied
 * @param {bool}   props.guaranteedResCardRequired - guaranteed res flag
 * @return {JSX}
 */
const ConfirmCancelReservationModal = () => {
  const appliedCancelFeeDetails = useSelector(appliedCancelFeeDetailsSelector);
  const prepaySelected = useSelector(getDidPrepay);
  const guaranteedResCardRequired = useSelector(guaranteedResCardRequiredSelector);
  const dispatchCancelReservation = useActions(cancelReservation);

  const renderGuaranteedResCancelFee = () => (
    <strong key={0}>{getFormattedPrice(appliedCancelFeeDetails.fee_amount_view)}</strong>
  );

  const renderCancellationMessage = () => {
    if (prepaySelected) {
      return <p className='confirmation__modal-cancel-copy'>{appliedCancelFeeDetails?.cancellation_message}</p>;
    }

    if (guaranteedResCardRequired && appliedCancelFeeDetails?.fee_apply) {
      return (
        <p className='modal-themed__section'>
          {i18n('guaranteed_res_cancel_modal_copy', [renderGuaranteedResCancelFee()], { jsx: true })}
        </p>
      );
    }

    return <p className='confirmation__modal-cancel-copy'>{i18n('confirmation_reservation_cancel_modal_copy')}</p>;
  };

  const renderPrepayCancellationRefund = () => {
    if (prepaySelected) {
      const { fee_amount_view, original_amount_view, refund_amount_view, fee_apply } = appliedCancelFeeDetails || {};
      return (
        <div className='confirmation__modal-cancel-refund'>
          <p className='refund-line-item'>
            <span className='refund-line-item__label'>{i18n('confirmation_reservation_cancel_original_amount')}</span>
            <FormattedPrice classNames='refund-line-item__value'>
              {getFormattedPrice(original_amount_view)}
            </FormattedPrice>
          </p>
          <p className='refund-line-item'>
            <span className='refund-line-item__label'>{i18n('confirmation_reservation_cancel_fee')}</span>
            <FormattedPrice classNames='refund-line-item__value'>
              {fee_apply ? getFormattedPrice(fee_amount_view) : getFormattedZeroPrice(fee_amount_view)}
            </FormattedPrice>
          </p>
          <p className='refund-line-item total'>
            <span className='refund-line-item__label'>{i18n('confirmation_reservation_cancel_amount_refunded')}</span>
            <FormattedPrice classNames='refund-line-item__value'>
              {fee_apply ? getFormattedPrice(refund_amount_view) : getFormattedPrice(original_amount_view)}
            </FormattedPrice>
          </p>
        </div>
      );
    }

    if (guaranteedResCardRequired && appliedCancelFeeDetails?.fee_apply) {
      return (
        <p className='modal-themed__section'>
          {i18n('guaranteed_res_cancel_modal_copy_2', [renderGuaranteedResCancelFee()], { jsx: true })}
        </p>
      );
    }

    return null;
  };

  const acceptAndClose = (handleClose) => () => {
    dispatchCancelReservation().then((response) => {
      if (!response.messages) {
        handleClose({ skipAnalytics: true });
      } else {
        loadingOverlay(false);
      }
    });
  };

  return (
    <Modal
      modalKey={MODAL.CONFIRM_CANCEL_RESERVATION_MODAL}
      header={i18n('confirmation_reservation_cancel_modal_header')}
      theme={MODAL_THEMES.WHITE}
    >
      {({ handleClose }) => (
        <>
          <ServiceErrors />
          <div id={`${MODAL.CONFIRM_CANCEL_RESERVATION_MODAL}_desc`}>{renderCancellationMessage()}</div>

          <div id={`${MODAL.CONFIRM_CANCEL_RESERVATION_MODAL}_desc`}>{renderPrepayCancellationRefund()}</div>

          <ModalFooter
            fullWidthButton
            buttons={[
              {
                type: DECLINE,
                label: i18n('confirmation_reservation_cancel_modal_decline'),
                analyticsValue: ANALYTICS.KEEP_RESERVATION,
                analyticsKey: ANALYTICS.CANCEL_RESERVATION_KEY,
              },
              {
                type: ACCEPT,
                label: i18n('confirmation_reservation_cancel_modal_accept'),
                handler: acceptAndClose(handleClose),
                analyticsHandler: () =>
                  analyticsInteraction(
                    ANALYTICS.MODAL,
                    ANALYTICS.CANCEL_RESERVATION_KEY,
                    ANALYTICS.CANCEL_RESERVATION_KEY
                  ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default ConfirmCancelReservationModal;
